import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { Periode } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { SetStateAction, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdWarning } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { InputState, storeDataPendaftaran } from "../DashboardSiswaPage";

export default function FormStudentExchange({
  form,
  setForm,
  onClose,
  selectedPeriode,
}: {
  form: InputState;
  setForm: React.Dispatch<SetStateAction<InputState>>;
  onClose: () => void;
  selectedPeriode: Periode;
}) {
  const { t } = useTranslation();

  const [_, setSessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const navigate = useNavigate();

  const [showConfirmDaftar, setShowConfirmDaftar] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [openListProgram, setOpenListProgram] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState("");

  const { dataPeriode } = useTimePeriode();
  const optionsProgram = dataPeriode?.sys_daftar_program?.map((item) => {
    return {
      label: item.ref_daftar_program.nama_program,
      value: item.id_ref_daftar_program,
    };
  });

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingStorePendaftaran,
    error,
  } = useMutation({
    mutationFn: storeDataPendaftaran,
    onSuccess: (data) => {
      flushSync(() => {
        setSessionPeriode({ id_sys_periode: data?.data?.id_sys_periode });
      });

      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({ queryKey: ["dataPendaftaran"] });
      queryClient.invalidateQueries({ queryKey: ["dataSiswa"] });
      navigate(data?.data?.redirect_url);
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      setShowConfirmDaftar(false);

      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleSubmitPendaftaran = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate({ ...form, id_ref_daftar_program: selectedProgram });
  };

  return (
    <div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setShowConfirmDaftar(true);
          setIsConfirm(false);
        }}
        className="flex flex-col gap-y-3 dark:text-slate-200"
      >
        <h1 className="mb-3 font-bold text-xl text-center text-slate-900 dark:text-white">
          {t("siswa.layout.register.title")}{" "}
          <span className="text-primary dark:text-blue-400">
            {selectedPeriode?.jalur_jenis?.nama_jalur_jenis}
          </span>
        </h1>
        <div>
          <Label required className="dark:text-slate-200">
            {t("siswa.layout.register.label.nama-lengkap")}
          </Label>
          <Input
            required
            placeholder={`type ${t(
              "siswa.layout.register.label.nama-lengkap",
            )}...`}
            value={form.nama_lengkap}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                nama_lengkap: event.target.value,
              }))
            }
            className="dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:placeholder:text-slate-500"
          />
          <p className="text-muted-foreground dark:text-slate-400 text-sm">
            {t("siswa.layout.register.label.nama-lengkap.description")}
          </p>
          <InputError
            text={error?.response?.data.errors?.nama_lengkap?.join(",")}
          />
        </div>
        <div>
          <Label className="dark:text-slate-200">Program</Label>
          <Combobox
            open={openListProgram}
            setOpen={setOpenListProgram}
            value={selectedProgram}
            setValue={(value) => {
              setSelectedProgram(value);
            }}
            options={optionsProgram || []}
            placeholder={`Select Program`}
          />
        </div>

        <div className="mt-5 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              onClose();
            }}
            className="dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            {t("siswa.layout.register.button.batal")}
          </Button>
          <Button
            variant={"default"}
            className="dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            {t("siswa.layout.register.button.daftar")}
          </Button>
        </div>
      </form>

      <Modal
        className="lg:w-[32rem] w-[90vw]"
        show={showConfirmDaftar}
        onClose={() => {
          setShowConfirmDaftar(false);
          setIsConfirm(false);
        }}
      >
        <FormHeader
          title={`${t("siswa.layout.register.confirmation.title")} ${
            selectedPeriode?.jalur_jenis?.nama_jalur_jenis
          }`}
          icon={<MdWarning />}
        />
        <form onSubmit={handleSubmitPendaftaran}>
          <div>
            <div className="mt-3 flex items-center gap-3">
              <Checkbox
                checked={isConfirm}
                onCheckedChange={(cheked) =>
                  setIsConfirm(cheked ? true : false)
                }
              />
              <span className={`${isConfirm ? "" : "text-destructive"}`}>
                {t("siswa.layout.register.confirmation.check")}
              </span>
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              onClick={() => {
                setShowConfirmDaftar(false);
                setIsConfirm(false);
              }}
              disabled={isPendingStorePendaftaran}
            >
              {t("siswa.layout.register.button.batal")}
            </Button>

            <Button
              className={`flex items-center gap-3 ${
                !isConfirm ? "opacity-50" : ""
              }`}
              disabled={isPendingStorePendaftaran || !isConfirm}
              variant={"default"}
            >
              {isPendingStorePendaftaran ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                t("siswa.layout.register.confirmation.button")
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
